import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiX, FiArrowRight } from 'react-icons/fi';
import { recitalService } from '../../../services/RecitalService';
import { actDurationSecs, formatSecs } from '../../../utils/recitalDuration';
import AddItemButton from './AddItemButton';
import ClassLibrary from './ClassLibrary';
import { resolveColor, lookStyles } from '../colorLook';

// The Assign Board ("corkboard") — mockup v8, approved Aug 20 2026.
// Shows are columns; the recital's classes (independent copies of schedule
// classes) live in the shared ClassLibrary on the left. Drag a class onto a
// show to place it, or click its show number; a class can live in several shows.
// Ordering happens in the Running order view.

const S = {
    wrap:     { display:'flex', flex:1, overflow:'hidden', background:'#eef0f7' },
    // Tray
    // Board
    boardScroll:{ flex:1, overflowX:'auto', display:'flex', gap:12, padding:14, alignItems:'stretch' },
    col:      (over) => ({
        width:262, flexShrink:0, background: over ? '#e6f7fd' : '#f7f8fc',
        border:`1px solid ${over ? '#29b8e5' : '#e0e0ec'}`, borderRadius:10,
        display:'flex', flexDirection:'column', overflow:'hidden', transition:'border-color .15s, background .15s',
        maxHeight:'100%',
    }),
    colHead:  { padding:'10px 12px 8px', borderBottom:'1px solid #eaeaf4', background:'#fff', flexShrink:0 },
    // Text sizes per mockup v6: Andrew, 2026-10-12, "a LOT of small text on the assign board".
    colTitle: { fontSize:15, fontWeight:700, color:'#3f3f55' },
    colMeta:  { fontSize:11, color:'#9b9bb8', marginTop:2 },
    colScroll:{ flex:1, overflowY:'auto', padding:8, minHeight:120 },
    card:     (color, dragging, look) => ({
        background:'#fff',
        border:'1px solid #e0e0ec',
        ...lookStyles(color, look).card,
        borderRadius:8, padding:'8px 10px', marginBottom:6, cursor:'grab', position:'relative', display:'flex', alignItems:'center', gap:8,
        boxShadow:'0 1px 2px rgba(40,40,60,0.08)', opacity: dragging ? 0.35 : 1, userSelect:'none',
    }),
    cardNum:  { fontSize:11, fontWeight:700, color:'#9999bb', width:16, flexShrink:0, textAlign:'right' },
    cardBody: { minWidth:0, flex:1, paddingRight:12 },
    cardName: { fontSize:13, fontWeight:600, color:'#3f3f55', lineHeight:1.25, display:'flex', alignItems:'center' },
    cardSub:  { fontSize:11, color:'#8a8aa8', lineHeight:1.3, marginTop:2 },
    cardX:    { position:'absolute', top:7, right:8, fontSize:12, color:'#b8b8d0', cursor:'pointer', lineHeight:1 },
    dropHint: (vis) => ({
        border:'1.5px dashed #c8c8e0', borderRadius:7, padding:'9px 8px', textAlign:'center',
        fontSize:11, color:'#a8b8e0', marginBottom:5, opacity: vis ? 1 : 0.55,
    }),
    colFoot:  { padding:'8px 12px', borderTop:'1px solid #eaeaf4', background:'#fff', fontSize:12, display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 },
    openLink: { color:'#29b8e5', cursor:'pointer', display:'flex', alignItems:'center', gap:3, fontWeight:600 },
    addCol:   { width:170, flexShrink:0, border:'1.5px dashed #c8c8e0', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', color:'#a0a0bc', fontSize:12, cursor:'pointer', background:'none', gap:5, minHeight:160 },
};

// A small colour dot, used when the look is "dot".
const Dot = ({ color }) => color
    ? <span style={{ display:'inline-block', width:9, height:9, borderRadius:'50%', background:color, marginRight:6, verticalAlign:'middle', flexShrink:0 }}/>
    : null;

export default function AssignBoard({ recital, shows, colorMap, colorToLevelName, onRefresh, onOpenShow, onAddShow, onAddItem, onToggleInShow, colorBy = 'style', colorLook = 'stripe' }) {
    const classes = recital?.classes || [];
    const [dragging, setDragging] = useState(null); // {classId, actId?, fromShowId?}
    const [overCol, setOverCol]   = useState(null);
    const [overAct, setOverAct]   = useState(null); // act id the pointer is over; drop lands BEFORE it
    const dragCounter = useRef({});

    // ── drag & drop ──────────────────────────────────────────────────────────
    const startTrayDrag = (cls) => setDragging({ classId: cls.id });
    const startCardDrag = (act, fromShowId) => setDragging({ classId: act.recitalClassId, actId: act.id, fromShowId, act });
    const endDrag = () => { setDragging(null); setOverCol(null); setOverAct(null); dragCounter.current = {}; };

    const enterCol = (e, showId) => {
        e.preventDefault();
        dragCounter.current[showId] = (dragCounter.current[showId] || 0) + 1;
        setOverCol(showId);
    };
    const leaveCol = (e, showId) => {
        dragCounter.current[showId] = (dragCounter.current[showId] || 0) - 1;
        if (dragCounter.current[showId] <= 0 && overCol === showId) setOverCol(null);
    };

    // Where a drop lands: before the card under the pointer, or last when the
    // pointer is on the column's empty space. Index is within the FULL running
    // order of the target show (intermissions included), with the moving act
    // itself taken out first so moving down does not land one slot short.
    const dropIndex = (show, d) => {
        const ordered = [...(show.acts || [])].sort((a, b) => a.position - b.position)
            .filter(a => a.id !== d.actId);
        if (overAct == null) return ordered.length;
        const i = ordered.findIndex(a => a.id === overAct);
        return i === -1 ? ordered.length : i;
    };

    const dropOnCol = async (e, show) => {
        e.preventDefault();
        dragCounter.current[show.id] = 0;
        setOverCol(null);
        const d = dragging;
        const target = dropIndex(show, d || {});
        setOverAct(null);
        setDragging(null);
        if (!d || !d.classId) return;

        const already = (show.acts || []).some(a => a.recitalClassId === d.classId && a.id !== d.actId);
        if (already) {
            toast.info('That class is already in this show.');
            return;
        }
        try {
            if (d.actId) {
                // Within this show or from another one: one move, exact spot, same act
                // (keeps its id, label and stage time). Andrew, 2026-09-18.
                await recitalService.moveAct(d.actId, show.id, target);
            } else {
                // From the tray: a brand new act. Create it, then slot it where it was dropped.
                const res = await recitalService.addAct(show.id, {
                    recitalClassId: d.classId,
                    type: 'class',
                    position: (show.acts || []).length,
                });
                const created = res?.data?.act;
                if (created?.id != null && target < (show.acts || []).length) {
                    await recitalService.moveAct(created.id, show.id, target);
                }
            }
            await onRefresh();
        } catch (err) {
            console.error('Assign failed', err);
            toast.error('Could not place that class. Please try again.');
            await onRefresh();
        }
    };

    const removeFromShow = async (act) => {
        try {
            await recitalService.deleteAct(act.id);
            await onRefresh();
        } catch (err) {
            console.error(err);
            toast.error('Could not remove that class from the show. Please try again.');
        }
    };

    const classById = (id) => classes.find(c => c.id === id);


    return (
        <div style={S.wrap}>
            <ClassLibrary
                recital={recital} shows={shows}
                colorBy={colorBy} colorLook={colorLook} colorMap={colorMap} colorToLevelName={colorToLevelName}
                onToggleInShow={onToggleInShow} onRefresh={onRefresh}
                onDragStart={startTrayDrag} onDragEnd={endDrag}
                draggingClassId={dragging && !dragging.actId ? dragging.classId : null}
            />

            {/* Columns */}
            <div style={S.boardScroll}>
                {shows.map((show, idx) => {
                    const acts = [...(show.acts || [])].sort((a, b) => a.position - b.position);
                    const classActs = acts.filter(a => a.type === 'class');
                    const totalSecs = acts.reduce((sum, a) => sum + (actDurationSecs(a) || 0), 0);
                    const isOver = overCol === show.id && !!dragging;
                    return (
                        <div key={show.id} style={S.col(isOver)}
                            onDragEnter={e => enterCol(e, show.id)}
                            onDragLeave={e => leaveCol(e, show.id)}
                            onDragOver={e => { e.preventDefault(); if (e.target === e.currentTarget) setOverAct(null); }}
                            onDrop={e => dropOnCol(e, show)}>
                            <div style={S.colHead}>
                                <div style={S.colTitle}>{show.name || `Show ${idx + 1}`}</div>
                                <div style={S.colMeta}>
                                    {classActs.length} {classActs.length === 1 ? 'class' : 'classes'}
                                    {totalSecs > 0 ? ` · ~${formatSecs(totalSecs)} stage time` : ''}
                                </div>
                            </div>
                            <div style={S.colScroll}>
                                {(() => { let n = 0; return acts.map(act => {
                                    const num = act.type === 'class' ? ++n : null;
                                    const landingHere = !!dragging && overCol === show.id && overAct === act.id && dragging.actId !== act.id;
                                    const landing = landingHere ? { boxShadow:'0 -2px 0 0 #29b8e5, 0 1px 2px rgba(40,40,60,0.08)' } : {};
                                    if (act.type !== 'class') {
                                        return (
                                            <div key={act.id}
                                                onDragOver={e => { e.preventDefault(); setOverAct(act.id); }}
                                                style={{ ...S.card(null, false, colorLook), ...landing, cursor:'default', background:'#fafafd', fontStyle:'italic' }}>
                                                <span style={S.cardNum}/>
                                                <div style={{ ...S.cardName, color:'#9b9bb8', fontWeight:500 }}>{act.label || act.type}</div>
                                            </div>
                                        );
                                    }
                                    const cls = act.recitalClass || classById(act.recitalClassId) || act.scheduleSticker;
                                    const color = resolveColor(cls, colorBy, colorMap);
                                    const marker = lookStyles(color, colorLook).marker;
                                    return (
                                        <div key={act.id}
                                            draggable={!!act.recitalClassId}
                                            onDragStart={() => startCardDrag(act, show.id)}
                                            onDragEnd={endDrag}
                                            onDragOver={e => { e.preventDefault(); setOverAct(act.id); }}
                                            style={{ ...S.card(color, dragging?.actId === act.id, colorLook), ...landing }}>
                                            <FiX size={11} style={S.cardX} title="Remove from this show"
                                                onClick={() => removeFromShow(act)}/>
                                            <span style={S.cardNum}>{num}</span>
                                            <div style={S.cardBody}>
                                                <div style={S.cardName}><Dot color={marker}/><span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cls?.className || act.label || 'Untitled act'}</span></div>
                                                <div style={S.cardSub}>{formatSecs(actDurationSecs(act)) || '2:30'}{cls?.students?.length ? ` · ${cls.students.length} dancers` : ''}</div>
                                            </div>
                                        </div>
                                    );
                                }); })()}
                                <div style={S.dropHint(isOver)}
                                    onDragOver={e => { e.preventDefault(); setOverAct(null); }}>
                                    {isOver ? (overAct == null ? 'Drop to add last' : 'Drop to add here') : 'drag a class here'}
                                </div>
                                {/* Intermission, custom moment or routine straight into this show.
                                    Andrew, 2026-10-12: the add option belongs on the board too. */}
                                {onAddItem && <AddItemButton onAdd={(type) => onAddItem(type, show)} />}
                            </div>
                            <div style={S.colFoot}>
                                <span style={{ color: classActs.length ? '#2ab568' : '#d4820a' }}>
                                    {classActs.length ? 'Has classes' : 'Empty'}
                                </span>
                                <span style={S.openLink} onClick={() => onOpenShow(idx)}>
                                    Open order <FiArrowRight size={10}/>
                                </span>
                            </div>
                        </div>
                    );
                })}
                <button style={S.addCol} onClick={onAddShow}>
                    <FiPlus size={13}/> Add show
                </button>
            </div>
        </div>
    );
}
